import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import {
  Upload,
  Download,
  Delete,
  AttachFile,
  Image,
  VideoLibrary,
  Description,
  Visibility,
  Schedule,
  Lock as LockClosed,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import {
  useGetEvidenceQuery,
  useGetEvidenceDeadlineStatusQuery,
  useUploadEvidenceMutation,
  useDeleteEvidenceMutation,
  useUpdateEvidenceMetadataMutation,
  useLazyDownloadEvidenceQuery,
  usePublicizeCaseEvidenceMutation,
  useGetEvidencePoolQuery,
  useCreateEvidenceFromPoolMutation,
  useLazyGetEvidenceAiSuggestionsQuery,
  useGetEvidenceLockStatusQuery,
  useOfficialSubmitEvidenceMutation,
  useReleaseEvidenceLockMutation,
  type EvidencePoolItem,
} from '../store/api/evidenceApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useLazyDownloadDocumentQuery } from '../store/api/documentsApi'
import { hasPermission } from '../utils/permissions'
import { formatCaseDisplay } from '../utils/caseDisplay'
import { PageHeader } from '../components/PageHeader'
import SubmissionDeadlineCountdown from '../components/SubmissionDeadlineCountdown'
import { getFilterPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'
import { gradientBrand } from '../theme/brand'
import { getApiErrorMessage } from '../utils/apiErrorMessage'

export default function EvidencePage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const [searchParams] = useSearchParams()
  const caseIdFromUrl = searchParams.get('caseId') || ''
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [selectedCaseId, setSelectedCaseId] = useState<string>(caseIdFromUrl)
  // BTC/Thư ký lọc theo đội — default '' = tất cả đội của case
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>('')

  useEffect(() => {
    if (caseIdFromUrl && caseIdFromUrl !== selectedCaseId) {
      setSelectedCaseId(caseIdFromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseIdFromUrl])
  const [uploadForm, setUploadForm] = useState({
    case_id: '',
    team_id: '' as string,
    title: '',
    description: '',
    category: '',
    file: null as File | null,
  })

  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [], isLoading: isLoadingTeams } = useGetTeamsQuery()
  const canUploadEvidence = currentUser && hasPermission(currentUser.role, 'UPLOAD_EVIDENCE')
  const canAssignCases = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')
  const userTeam = teams.find((t) => t.members.includes(currentUser?.id || ''))
  const { data: evidence = [], isLoading, refetch } = useGetEvidenceQuery(
    { case_id: selectedCaseId || undefined },
    {
      skip:
        (!canAssignCases && !selectedCaseId) ||
        (!canAssignCases && !!canUploadEvidence && !userTeam),
    },
  )

  const { data: poolItems = [], isLoading: poolLoading } = useGetEvidencePoolQuery(selectedCaseId, {
    skip: !selectedCaseId,
  })
  const [createFromPool, { isLoading: isAddingFromPool }] = useCreateEvidenceFromPoolMutation()

  const canUseAiSuggestions =
    currentUser &&
    (hasPermission(currentUser.role, 'VIEW_AI_REPORTS') ||
      hasPermission(currentUser.role, 'SCORE_CASES') ||
      hasPermission(currentUser.role, 'ASSIGN_CASES'))

  const skipAiSuggestions = !selectedCaseId || !canUseAiSuggestions

  const [aiKeyword, setAiKeyword] = useState('')
  const [
    fetchAiSuggestions,
    {
      data: aiSuggestions,
      isFetching: aiLoading,
      isError: aiSuggestionsError,
      error: aiSuggestionsErr,
    },
  ] = useLazyGetEvidenceAiSuggestionsQuery()

  const [poolDropHover, setPoolDropHover] = useState(false)

  useEffect(() => {
    setAiKeyword('')
  }, [selectedCaseId])

  useEffect(() => {
    if (skipAiSuggestions || !selectedCaseId) return
    fetchAiSuggestions({ case_id: selectedCaseId })
  }, [selectedCaseId, skipAiSuggestions, fetchAiSuggestions])

  const [uploadEvidence, { isLoading: isUploading }] = useUploadEvidenceMutation()
  const [deleteEvidence] = useDeleteEvidenceMutation()
  const [updateEvidenceMetadata] = useUpdateEvidenceMetadataMutation()
  const [downloadDocument] = useLazyDownloadDocumentQuery()
  const [officialSubmitEvidence, { isLoading: isOfficialSubmitting }] =
    useOfficialSubmitEvidenceMutation()
  const [releaseEvidenceLock, { isLoading: isReleasingLock }] = useReleaseEvidenceLockMutation()

  // Preview tài liệu pool (DOCX → render HTML qua mammoth, các loại khác → blob URL trong iframe)
  const [previewDoc, setPreviewDoc] = useState<{
    open: boolean
    title: string
    fileName: string
    mimeType: string
    html?: string
    blobUrl?: string
    loading: boolean
    error?: string
  }>({ open: false, title: '', fileName: '', mimeType: '', loading: false })

  const handleDownloadDocument = async (docId: string, fileName: string) => {
    try {
      const blob = await downloadDocument(docId).unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: `Tải về thất bại: ${getApiErrorMessage(e, '')}`,
        severity: 'error',
      })
    }
  }

  const handlePreviewDocument = async (
    docId: string,
    title: string,
    fileName: string,
    mimeType: string,
  ) => {
    setPreviewDoc({ open: true, title, fileName, mimeType, loading: true })
    try {
      const blob = await downloadDocument(docId).unwrap()
      const lower = fileName.toLowerCase()
      const isDocx =
        lower.endsWith('.docx') ||
        mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      if (isDocx) {
        const arrayBuffer = await blob.arrayBuffer()
        const mammoth = await import('mammoth')
        const result = await mammoth.convertToHtml({ arrayBuffer })
        setPreviewDoc({
          open: true,
          title,
          fileName,
          mimeType,
          loading: false,
          html: result.value || '<em>Tài liệu trống.</em>',
        })
      } else {
        // PDF / image / etc → blob URL
        const blobUrl = URL.createObjectURL(blob)
        setPreviewDoc({
          open: true,
          title,
          fileName,
          mimeType,
          loading: false,
          blobUrl,
        })
      }
    } catch (e: any) {
      setPreviewDoc({
        open: true,
        title,
        fileName,
        mimeType,
        loading: false,
        error: getApiErrorMessage(e, 'Không tải được tài liệu'),
      })
    }
  }

  const handleClosePreview = () => {
    if (previewDoc.blobUrl) URL.revokeObjectURL(previewDoc.blobUrl)
    setPreviewDoc({ open: false, title: '', fileName: '', mimeType: '', loading: false })
  }
  const [downloadEvidence] = useLazyDownloadEvidenceQuery()
  const [publicizeCase, { isLoading: isPublicizing }] = usePublicizeCaseEvidenceMutation()
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })
  
  // View evidence dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [viewingEvidence, setViewingEvidence] = useState<any>(null)

  // Filter cases assigned to user's team
  const userCases = cases.filter((c) => {
    if (!userTeam) return false
    return c.assigned_team_ids?.includes(userTeam.id)
  })

  // Banner deadline chỉ query khi user CHỦ ĐỘNG chọn case — không fallback userCases[0]
  // (tránh hiện "Hết hạn" cho case ngẫu nhiên user chưa quan tâm)
  const caseIdForEvidenceDeadline = selectedCaseId || ''
  const { data: evidenceDeadlineStatus } = useGetEvidenceDeadlineStatusQuery(caseIdForEvidenceDeadline, {
    skip: !caseIdForEvidenceDeadline,
    pollingInterval: caseIdForEvidenceDeadline ? 1000 : undefined,
  })
  const evidenceSubmissionOpen =
    !evidenceDeadlineStatus || evidenceDeadlineStatus.submission_open

  const { data: uploadDialogDeadline } = useGetEvidenceDeadlineStatusQuery(uploadForm.case_id, {
    skip: !uploadDialogOpen || !uploadForm.case_id,
    pollingInterval: uploadDialogOpen && uploadForm.case_id ? 1000 : undefined,
  })
  const uploadDialogSubmissionOpen =
    !uploadDialogDeadline || uploadDialogDeadline.submission_open

  // Lock status (case + team đang xem). Đội xem lock của chính mình; BTC xem lock theo team đang lọc.
  const lockTeamId = userTeam?.id || (canAssignCases ? selectedTeamFilter : '') || ''
  const { data: evidenceLockStatus } = useGetEvidenceLockStatusQuery(
    { case_id: selectedCaseId, team_id: lockTeamId },
    { skip: !selectedCaseId || !lockTeamId },
  )
  const isTeamEvidenceLocked = !!evidenceLockStatus?.locked
  // Đội thi không được thao tác khi đã locked; BTC vẫn được (sau khi release).
  const canMutateEvidence =
    !isTeamEvidenceLocked || !!canAssignCases
  const canPublicizeEvidence =
    currentUser &&
    (canAssignCases || hasPermission(currentUser.role, 'MANAGE_USERS'))

  // Dropdown lọc theo cuộc thi: BTC/Thư ký thấy tất cả; đội thi chỉ thấy case của đội mình
  const filterableCases = canAssignCases ? cases : userCases

  // BR-03: đội thi chỉ xem chứng cứ thuộc case của đội mình (kể cả evidence đã public từ cặp khác).
  // BTC/admin thấy đầy đủ — nhưng có thêm filter "theo đội" để xem riêng từng đội.
  const userCaseIds = useMemo(() => new Set(userCases.map((c) => c.id)), [userCases])

  // Danh sách đội thuộc case đang chọn (cho BTC filter)
  const teamsInSelectedCase = useMemo(() => {
    if (!selectedCaseId) return [] as typeof teams
    const c = cases.find((x) => x.id === selectedCaseId)
    if (!c?.assigned_team_ids) return []
    return teams.filter((t) => c.assigned_team_ids?.includes(t.id))
  }, [selectedCaseId, cases, teams])

  const displayEvidence = useMemo(() => {
    let filtered = canAssignCases
      ? evidence
      : userTeam
        ? evidence.filter(
            (e) => e.team_id === userTeam.id && e.case_id && userCaseIds.has(e.case_id),
          )
        : []
    // BTC/admin: lọc thêm theo đội nếu có chọn
    if (canAssignCases && selectedTeamFilter) {
      filtered = filtered.filter((e) => e.team_id === selectedTeamFilter)
    }
    // Sort: mới nhất lên đầu — dùng Date.getTime() thay vì string compare,
    // vì format ISO có thể khác (BE: '2026-05-09T...123456', FE optimistic: '...123Z')
    // làm localeCompare cho kết quả sai (Z > số) → placeholder bị sort xuống cuối.
    return [...filtered].sort((a, b) => {
      const aT = new Date(a.submitted_at || a.uploaded_at || 0).getTime() || 0
      const bT = new Date(b.submitted_at || b.uploaded_at || 0).getTime() || 0
      return bT - aT
    })
  }, [evidence, canAssignCases, userCaseIds, selectedTeamFilter, userTeam])

  const publicEvidence = useMemo(() => {
    if (!userTeam || canAssignCases) return []
    return evidence
      .filter(
        (e) =>
          e.team_id !== userTeam.id &&
          e.is_public &&
          e.case_id &&
          userCaseIds.has(e.case_id),
      )
      .sort((a, b) => {
        const aT = new Date(a.submitted_at || a.uploaded_at || 0).getTime() || 0
        const bT = new Date(b.submitted_at || b.uploaded_at || 0).getTime() || 0
        return bT - aT
      })
  }, [evidence, canAssignCases, userCaseIds, userTeam])

  // Reset team filter khi đổi case
  useEffect(() => {
    setSelectedTeamFilter('')
  }, [selectedCaseId])

  // Phân loại chứng cứ theo mục đích sử dụng trong Đơn khởi kiện (Moot Court HNGĐ)
  const EVIDENCE_CATEGORIES = [
    'Quan hệ hôn nhân & con chung',
    'Phân chia tài sản',
    'Quyền nuôi con & cấp dưỡng',
    'Mâu thuẫn / vi phạm chung thủy',
    'Tài chính & thu nhập',
    'Giấy tờ pháp lý',
    'Khác',
  ]

  const handleOpenUploadDialog = () => {
    if (canUploadEvidence && userTeam && !evidenceSubmissionOpen) {
      setSnackbar({
        open: true,
        message: evidenceDeadlineStatus?.message || 'Hết hạn nộp chứng cứ.',
        severity: 'error',
      })
      return
    }
    const preferred =
      (selectedCaseId && userCases.find((c) => c.id === selectedCaseId)?.id) ||
      userCases[0]?.id ||
      ''
    if (preferred) {
      setUploadForm({
        ...uploadForm,
        case_id: preferred,
        team_id: userTeam?.id || '',
      })
    }
    setUploadDialogOpen(true)
  }

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false)
    setUploadForm({
      case_id: '',
      team_id: userTeam?.id || '',
      title: '',
      description: '',
      category: '',
      file: null,
    })
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setUploadForm({ ...uploadForm, file: event.target.files[0] })
    }
  }

  const handleUpload = async () => {
    setSnackbar({ ...snackbar, open: false })

    if (canUploadEvidence && !uploadDialogSubmissionOpen) {
      setSnackbar({
        open: true,
        message: uploadDialogDeadline?.message || 'Hết hạn nộp chứng cứ.',
        severity: 'error',
      })
      return
    }

    if (!uploadForm.file || !uploadForm.title || !uploadForm.case_id || !uploadForm.team_id) {
      setSnackbar({
        open: true,
        message: 'Vui lòng điền đầy đủ thông tin',
        severity: 'error',
      })
      return
    }

    const selectedCase = cases.find((c) => c.id === uploadForm.case_id)

    // Auto-generate tags: team, case, case_number (if available)
    const autoTags: string[] = []
    if (uploadForm.team_id) {
      const team = teams.find((t) => t.id === uploadForm.team_id)
      if (team) {
        autoTags.push(`team:${team.name}`)
      }
    }
    if (uploadForm.case_id) {
      if (selectedCase) {
        autoTags.push(`case:${formatCaseDisplay(selectedCase)}`)
      }
      if (selectedCase?.case_number) {
        autoTags.push(`case_number:${selectedCase.case_number}`)
      }
    }
    // Round sẽ được thêm sau nếu có field round trong case

    const formData = new FormData()
    formData.append('file', uploadForm.file)
    formData.append('case_id', uploadForm.case_id)
    formData.append('team_id', uploadForm.team_id)
    formData.append('title', uploadForm.title)
    if (uploadForm.description) formData.append('description', uploadForm.description)
    if (uploadForm.category) formData.append('category', uploadForm.category)
    // Append auto-generated tags
    autoTags.forEach((tag) => formData.append('tags[]', tag))

    try {
      await uploadEvidence(formData).unwrap()
      handleCloseUploadDialog()
      // RTK Query auto-refetch theo invalidatesTags['Evidence'] — không cần manual refetch.
      // Trước đây có setTimeout(refetch, 500) gây delay UI thêm 0.5s không cần thiết.
      setSnackbar({
        open: true,
        message: 'Upload chứng cứ thành công!',
        severity: 'success',
      })
    } catch (error: unknown) {
      setSnackbar({
        open: true,
        message: `Upload thất bại: ${getApiErrorMessage(error)}`,
        severity: 'error',
      })
    }
  }

  // Tránh double-click khi user bấm Delete nhanh tay
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set())
  const handleDelete = async (evidenceId: string) => {
    if (deletingIds.has(evidenceId)) return  // đang xử lý — bỏ qua
    if (!confirm('Bạn có chắc muốn xóa chứng cứ này?')) return

    setDeletingIds((prev) => new Set(prev).add(evidenceId))
    try {
      await deleteEvidence(evidenceId).unwrap()
      setSnackbar({
        open: true,
        message: 'Đã xóa chứng cứ.',
        severity: 'success',
      })
    } catch (error: any) {
      const status = error?.status ?? error?.error?.status
      // 404 = item đã không tồn tại (vd. xóa rồi) → coi như thành công
      if (status === 404) {
        setSnackbar({
          open: true,
          message: 'Chứng cứ đã được xóa trước đó.',
          severity: 'info',
        })
      } else {
        setSnackbar({
          open: true,
          message: `Xóa thất bại: ${error?.data?.detail || error?.message || 'Lỗi không xác định'}`,
          severity: 'error',
        })
      }
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev)
        next.delete(evidenceId)
        return next
      })
    }
  }
  
  const handleDownload = async (evidenceId: string, fileName: string) => {
    try {
      const result = await downloadEvidence(evidenceId).unwrap()
      const url = window.URL.createObjectURL(result)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setSnackbar({
        open: true,
        message: 'Download thành công!',
        severity: 'success',
      })
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Download thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }
  
  const handleViewEvidence = (item: any) => {
    setViewingEvidence(item)
    setViewDialogOpen(true)
  }
  
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false)
    setViewingEvidence(null)
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <Image />
    if (mimeType.startsWith('video/')) return <VideoLibrary />
    return <Description />
  }

  const handleAddFromPool = async (doc: EvidencePoolItem) => {
    if (!userTeam || !selectedCaseId) return
    if (canUploadEvidence && !evidenceSubmissionOpen) {
      setSnackbar({
        open: true,
        message: evidenceDeadlineStatus?.message || 'Hết hạn nộp chứng cứ.',
        severity: 'error',
      })
      return
    }
    // Pick thẳng vào Bản phụ lục — không còn yêu cầu chọn category (đã bỏ tiêu chí Phân loại).
    try {
      await createFromPool({
        case_id: selectedCaseId,
        team_id: userTeam.id,
        document_id: doc.document_id,
        title: doc.title,
      }).unwrap()
      setSnackbar({
        open: true,
        message: 'Đã thêm chứng cứ vào Bản phụ lục.',
        severity: 'success',
      })
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, 'Thêm từ pool thất bại'),
        severity: 'error',
      })
    }
  }

  const handlePoolDragStart = (e: React.DragEvent, row: EvidencePoolItem) => {
    e.dataTransfer.setData('application/ecourt-pool-item', JSON.stringify(row))
    e.dataTransfer.effectAllowed = 'copy'
  }

  const parseDroppedPoolItem = (e: React.DragEvent): EvidencePoolItem | null => {
    try {
      const raw = e.dataTransfer.getData('application/ecourt-pool-item')
      if (!raw) return null
      const o = JSON.parse(raw) as EvidencePoolItem
      if (!o.document_id || o.case_id !== selectedCaseId) return null
      return o
    } catch {
      return null
    }
  }

  const handlePoolDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setPoolDropHover(false)
    const row = parseDroppedPoolItem(e)
    if (!row || !canUploadEvidence || !userTeam || !evidenceSubmissionOpen) return
    await handleAddFromPool(row)
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        mt: { xs: 2, sm: 3 },
        mb: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 3 },
        // Chặn phần tử con (bảng nhiều cột, description dài) đẩy container vượt viewport
        overflowX: 'hidden',
      }}
    >
      <PageHeader
        title="Chứng cứ"
        subtitle="Đội thi chọn các tài liệu trong pool BTC cung cấp làm chứng cứ. KHÔNG upload file ngoài (BR Vòng Sơ loại 9.3)."
        icon={<AttachFile />}
        actions={
          // Upload button CHỈ dành cho admin/secretary — đội thi không được upload ngoài pool ở vòng Sơ loại
          canAssignCases ? (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Upload />}
              onClick={handleOpenUploadDialog}
              disabled={userCases.length === 0 || !evidenceSubmissionOpen}
              sx={{ textTransform: 'none' }}
            >
              Upload (BTC)
            </Button>
          ) : null
        }
      />

      {!canUploadEvidence && !canAssignCases && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Chỉ sinh viên thuộc đội thi mới chọn được chứng cứ từ pool.
        </Alert>
      )}

      {canUploadEvidence && userCases.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Đội bạn chưa được phân vào cuộc thi nào. Liên hệ Ban tổ chức.
        </Alert>
      )}

      {/* Filter — dùng danh sách case theo API (đã lọc theo vai trò) */}
      <Paper
        elevation={0}
        sx={(theme) => ({
          ...getFilterPaperSx(theme),
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            md: canAssignCases && selectedCaseId && teamsInSelectedCase.length > 0
              ? canPublicizeEvidence
                ? 'minmax(260px, 1fr) minmax(240px, 0.8fr) auto'
                : 'minmax(260px, 1fr) minmax(240px, 0.8fr)'
              : canPublicizeEvidence
                ? 'minmax(260px, 1fr) auto'
                : '1fr',
          },
          alignItems: 'center',
        })}
      >
        <FormControl fullWidth>
          <InputLabel>Lọc theo cuộc thi</InputLabel>
          <Select
            value={selectedCaseId}
            label="Lọc theo cuộc thi"
            onChange={(e) => setSelectedCaseId(e.target.value)}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {filterableCases.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {formatCaseDisplay(c)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Filter theo Đội — chỉ BTC/Thư ký thấy.
            Mỗi đội chọn chứng cứ RIÊNG → cần lọc để xem từng đội thay vì gộp chung */}
        {canAssignCases && selectedCaseId && teamsInSelectedCase.length > 0 && (
          <FormControl fullWidth>
            <InputLabel>Lọc theo đội</InputLabel>
            <Select
              value={selectedTeamFilter}
              label="Lọc theo đội"
              onChange={(e) => setSelectedTeamFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>— Tất cả đội ({teamsInSelectedCase.length}) —</em>
              </MenuItem>
              {teamsInSelectedCase.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {canPublicizeEvidence && (
          <Button
            variant="outlined"
            color="secondary"
            disabled={!selectedCaseId || isPublicizing}
            onClick={async () => {
              if (!selectedCaseId) return
              const targetTeamIds = selectedTeamFilter ? [selectedTeamFilter] : undefined
              const targetTeamName = selectedTeamFilter
                ? teamsInSelectedCase.find((t) => t.id === selectedTeamFilter)?.name || 'đội đang lọc'
                : teamsInSelectedCase.length
                  ? `tất cả ${teamsInSelectedCase.length} đội`
                  : 'tất cả đội'
              try {
                const res = await publicizeCase({
                  case_id: selectedCaseId,
                  team_ids: targetTeamIds,
                }).unwrap()
                setSnackbar({
                  open: true,
                  message: `Đã công khai ${res.updated} chứng cứ cho ${targetTeamName}.`,
                  severity: 'success',
                })
                refetch()
              } catch (error: any) {
                setSnackbar({
                  open: true,
                  message: getApiErrorMessage(error, 'Lỗi công khai'),
                  severity: 'error',
                })
              }
            }}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', justifySelf: { md: 'end' } }}
          >
            {selectedTeamFilter ? 'Công khai cho đội đang lọc' : 'Công khai cho tất cả đội'}
          </Button>
        )}
      </Paper>

      {/* Khi quá hạn — vẫn dùng Alert lớn vì critical */}
      {canUploadEvidence &&
        userTeam &&
        evidenceDeadlineStatus &&
        !evidenceDeadlineStatus.submission_open && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Đã quá ngày kết thúc cuộc thi
            </Typography>
            <Typography variant="body2">{evidenceDeadlineStatus.message}</Typography>
          </Alert>
        )}

      {/* Khi đang trong hạn — Chip nhỏ ở góc, hover để xem chi tiết */}
      {canUploadEvidence &&
        userTeam &&
        evidenceDeadlineStatus &&
        evidenceDeadlineStatus.submission_open &&
        Boolean(caseIdForEvidenceDeadline) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Tooltip
              arrow
              placement="left"
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                    {evidenceDeadlineStatus.message}
                  </Typography>
                  <SubmissionDeadlineCountdown status={evidenceDeadlineStatus} />
                </Box>
              }
            >
              <Chip
                size="small"
                color="success"
                variant="outlined"
                icon={<Schedule fontSize="small" />}
                label="Đang mở nhận chứng cứ"
                sx={{ cursor: 'help' }}
              />
            </Tooltip>
          </Box>
        )}

      {/* Stats bar — visual feedback đã chọn N/M */}
      {selectedCaseId && userTeam && (() => {
        const totalPool = poolItems.length
        const teamPickedFromPool = displayEvidence.filter(
          (e: any) =>
            e.team_id === userTeam.id &&
            (e.tags || []).some((t: string) => typeof t === 'string' && t.startsWith('from_pool_document:')),
        )
        const pickedDocIds = new Set(
          teamPickedFromPool.flatMap((e: any) =>
            (e.tags || [])
              .filter((t: string) => typeof t === 'string' && t.startsWith('from_pool_document:'))
              .map((t: string) => t.split(':', 2)[1]),
          ),
        )
        const pickedCount = pickedDocIds.size
        const remaining = Math.max(0, totalPool - pickedCount)
        const pct = totalPool ? Math.round((pickedCount / totalPool) * 100) : 0
        const StatCard = ({ label, value, color = 'primary' }: { label: string; value: React.ReactNode; color?: 'primary' | 'success' | 'warning' | 'info' }) => (
          <Paper variant="outlined" sx={{ p: 1.5, flex: 1, borderRadius: 2, borderTopWidth: 3, borderTopColor: `${color}.main` }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={700} color={`${color}.main`} sx={{ mt: 0.25, lineHeight: 1.2 }}>
              {value}
            </Typography>
          </Paper>
        )
        return (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <StatCard label="Tổng pool BTC" value={totalPool} color="info" />
            <StatCard label="Đội đã chọn" value={pickedCount} color="success" />
            <StatCard label="Còn lại" value={remaining} color="warning" />
            <StatCard label="% Đã chọn" value={`${pct}%`} color="primary" />
          </Box>
        )
      })()}

      {/* 2-COL LAYOUT: Pool tài liệu (trái) ↔ Chứng cứ đội đã chọn (phải) */}
      {selectedCaseId && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 2,
            mb: 3,
            alignItems: 'stretch', // 2 cột tự co dãn theo bên cao nhất
          }}
        >
      {selectedCaseId && (
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 600 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              Chứng cứ từ đề thi
            </Typography>
            <Chip
              size="small"
              label={`${poolItems.length} tài liệu`}
              color="info"
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Danh sách tài liệu Ban tổ chức cung cấp cho cuộc thi. Đội thi xem nội dung,
            sàng lọc và bấm <strong>"Chọn"</strong> để đính kèm tài liệu phù hợp vào hồ sơ chứng cứ của đội.
          </Typography>
          {poolLoading ? (
            <CircularProgress size={28} />
          ) : poolItems.length === 0 ? (
            (() => {
              // Phân biệt nguyên nhân pool empty cho student
              const selectedCase = cases.find((c: any) => c.id === selectedCaseId)
              const teamAssigned = !!userTeam && (selectedCase?.assigned_team_ids || []).includes(userTeam.id)
              const teamApproved = !!userTeam && ['active', 'confirmed', 'completed'].includes(userTeam.status || '')
              if (!userTeam) {
                return (
                  <Alert severity="warning">
                    Bạn chưa thuộc đội thi nào. Đăng ký đội tại trang <strong>Đội thi</strong> trước.
                  </Alert>
                )
              }
              if (!teamAssigned) {
                return (
                  <Alert severity="warning">
                    Đội của bạn chưa được phân vào cuộc thi này. Liên hệ Ban tổ chức để được phân bổ.
                  </Alert>
                )
              }
              if (!teamApproved) {
                return (
                  <Alert severity="info">
                    Đội của bạn đang ở trạng thái <strong>{userTeam.status}</strong> — chờ Ban tổ chức duyệt
                    hồ sơ trước khi được xem pool tài liệu đề. Sau khi duyệt, pool chứng cứ sẽ tự xuất hiện ở đây.
                  </Alert>
                )
              }
              return (
                <Alert severity="warning">
                  BTC chưa nạp pool chứng cứ cho cuộc thi này (chưa có tài liệu nào trong pool).
                  Liên hệ BTC nếu thiếu.
                </Alert>
              )
            })()
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                alignItems: 'stretch',
              }}
            >
              <TableContainer
                sx={(theme) => ({
                  ...getTablePaperSx(theme),
                  flex: 1,
                  minWidth: 0,
                  overflowX: 'auto',
                  // Fixed height ~10 records (mỗi row ~50px + header ~52px)
                  maxHeight: 552,
                  overflowY: 'auto',
                })}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Tiêu đề</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'background.paper', fontWeight: 600, minWidth: 220 }}>Thao tác</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Tính map document_id → đã chọn để disable row + show check
                      const pickedDocIds = new Set<string>(
                        displayEvidence
                          .filter((e: any) => e.team_id === userTeam?.id)
                          .flatMap((e: any) =>
                            (e.tags || [])
                              .filter((t: string) => typeof t === 'string' && t.startsWith('from_pool_document:'))
                              .map((t: string) => t.split(':', 2)[1]),
                          ),
                      )
                      return poolItems.map((row) => {
                        const isPicked = pickedDocIds.has(row.document_id)
                        const canDrag = Boolean(canUploadEvidence && userTeam && evidenceSubmissionOpen) && !isPicked
                        return (
                          <TableRow
                            key={row.document_id}
                            hover
                            draggable={canDrag}
                            onDragStart={(e) => handlePoolDragStart(e, row)}
                            sx={{
                              cursor: canDrag ? 'grab' : 'default',
                              opacity: isPicked ? 0.55 : 1,
                              bgcolor: isPicked ? 'action.hover' : 'transparent',
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isPicked && (
                                  <Chip size="small" label="✓" color="success" variant="filled" sx={{ minWidth: 28 }} />
                                )}
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ textDecoration: isPicked ? 'line-through' : 'none', fontWeight: 500 }}
                                    noWrap
                                  >
                                    {row.title}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {row.file_name}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <IconButton
                                size="small"
                                title="Xem nội dung"
                                onClick={() => handlePreviewDocument(row.document_id, row.title, row.file_name, row.mime_type)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                title="Tải về"
                                onClick={() => handleDownloadDocument(row.document_id, row.file_name)}
                              >
                                <Download fontSize="small" />
                              </IconButton>
                              {isPicked ? (
                                <Chip
                                  size="small"
                                  label="Đã chọn"
                                  color="success"
                                  variant="outlined"
                                  sx={{ ml: 0.5 }}
                                />
                              ) : (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  disabled={
                                    !canUploadEvidence ||
                                    !userTeam ||
                                    isAddingFromPool ||
                                    !evidenceSubmissionOpen ||
                                    !canMutateEvidence
                                  }
                                  onClick={() => handleAddFromPool(row)}
                                  sx={{ textTransform: 'none', ml: 0.5 }}
                                  title={!canMutateEvidence ? 'Đội đã nộp chính thức — đã khóa.' : undefined}
                                >
                                  Chọn
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      )}

          {/* RIGHT col — Chứng cứ đội đã chọn */}
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 600 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {canAssignCases
                    ? selectedTeamFilter
                      ? `Chứng cứ — ${teamsInSelectedCase.find((t) => t.id === selectedTeamFilter)?.name || ''}`
                      : 'Chứng cứ tất cả các đội'
                    : 'Chứng cứ đội đã chọn'}
                </Typography>
                {canAssignCases && !selectedTeamFilter && teamsInSelectedCase.length > 1 && (
                  <Typography variant="caption" color="text.secondary">
                    Mỗi đội có chứng cứ RIÊNG — chọn đội ở dropdown trên để xem từng đội.
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={`${displayEvidence.length} chứng cứ`}
                  color="success"
                  variant="outlined"
                />
                {selectedCaseId && lockTeamId && isTeamEvidenceLocked && (
                  <Chip
                    size="small"
                    icon={<LockClosed fontSize="small" />}
                    label="Đã nộp chính thức"
                    color="success"
                  />
                )}
                {/* Nút "Nộp chính thức" — đội thi, có evidence, còn deadline, chưa locked */}
                {selectedCaseId &&
                  canUploadEvidence &&
                  userTeam &&
                  evidenceSubmissionOpen &&
                  !isTeamEvidenceLocked &&
                  displayEvidence.some((e: any) => e.team_id === userTeam.id) && (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disabled={isOfficialSubmitting}
                      onClick={async () => {
                        const ok = window.confirm(
                          'Bạn xác nhận nộp chính thức danh sách chứng cứ hiện tại?\n\n' +
                            'Sau khi nộp, đội KHÔNG thể thêm hoặc xóa chứng cứ. ' +
                            'Chỉ Ban tổ chức được mở khóa trong trường hợp ngoại lệ.',
                        )
                        if (!ok) return
                        try {
                          await officialSubmitEvidence({
                            case_id: selectedCaseId,
                            team_id: userTeam.id,
                          }).unwrap()
                          setSnackbar({
                            open: true,
                            message: 'Đã nộp chứng cứ chính thức.',
                            severity: 'success',
                          })
                        } catch (e: any) {
                          setSnackbar({
                            open: true,
                            message: e?.data?.detail || e?.message || 'Nộp chính thức thất bại',
                            severity: 'error',
                          })
                        }
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      {isOfficialSubmitting ? <CircularProgress size={18} /> : 'Nộp chính thức'}
                    </Button>
                  )}
                {/* BTC mở khóa khi đã locked */}
                {selectedCaseId && lockTeamId && isTeamEvidenceLocked && canAssignCases && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={isReleasingLock}
                    onClick={async () => {
                      const reason = window.prompt('Lý do mở khóa (sẽ ghi vào audit log):', '')
                      if (reason === null) return
                      try {
                        await releaseEvidenceLock({
                          case_id: selectedCaseId,
                          team_id: lockTeamId,
                          reason: reason || undefined,
                        }).unwrap()
                        setSnackbar({
                          open: true,
                          message: 'Đã mở khóa nộp chứng cứ cho đội.',
                          severity: 'success',
                        })
                      } catch (e: any) {
                        setSnackbar({
                          open: true,
                          message: e?.data?.detail || e?.message || 'Mở khóa thất bại',
                          severity: 'error',
                        })
                      }
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Mở khóa (BTC)
                  </Button>
                )}
              </Box>
            </Box>
            {/* Subtitle / lock detail */}
            {selectedCaseId && lockTeamId && isTeamEvidenceLocked ? (
              <Alert severity="success" variant="outlined" sx={{ mb: 2 }} icon={<LockClosed fontSize="small" />}>
                Đã khóa danh sách chứng cứ
                {evidenceLockStatus?.total_items != null && <> — tổng {evidenceLockStatus.total_items} mục</>}
                {evidenceLockStatus?.locked_at && (
                  <> ({new Date(evidenceLockStatus.locked_at).toLocaleString('vi-VN')})</>
                )}
                . Đội không thể thêm/sửa/xóa.
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Hồ sơ chứng cứ đội đã đính kèm cho cuộc thi. Đội chọn các tài liệu
                phù hợp để Hội đồng Giám khảo đánh giá.
              </Typography>
            )}
            {isLoading || isLoadingTeams ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : displayEvidence.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderStyle: 'dashed',
                  borderRadius: 2,
                  borderColor: 'divider',
                }}
              >
                <AttachFile sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Chưa có chứng cứ nào
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Bấm <strong>"Chọn"</strong> ở pool bên trái để đính kèm vào hồ sơ đội.
                </Typography>
              </Paper>
            ) : (
              <TableContainer
                sx={(theme) => ({
                  ...getTablePaperSx(theme),
                  maxHeight: 552,
                  overflowY: 'auto',
                })}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: 'background.paper' }}>Tiêu đề</TableCell>
                      {/* Cột "Đội" — chỉ hiện cho BTC/Thư ký để biết chứng cứ thuộc đội nào */}
                      {canAssignCases && (
                        <TableCell sx={{ bgcolor: 'background.paper', minWidth: 140 }}>Đội</TableCell>
                      )}
                      
                      <TableCell align="right" sx={{ bgcolor: 'background.paper', minWidth: 110 }}>Thao tác</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayEvidence.map((item) => {
                      const isOwnerOrBtc =
                        (userTeam && userTeam.id === item.team_id) || canAssignCases
                      const itemTeam = canAssignCases
                        ? teams.find((t) => t.id === item.team_id)
                        : null
                      return (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500} noWrap title={item.title}>
                              {item.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {item.file_name}
                            </Typography>
                          </TableCell>
                          {canAssignCases && (
                            <TableCell>
                              {itemTeam ? (
                                <Chip
                                  size="small"
                                  label={itemTeam.name}
                                  color="default"
                                  variant="outlined"
                                  onClick={() => setSelectedTeamFilter(itemTeam.id)}
                                  sx={{ cursor: 'pointer', maxWidth: 140 }}
                                  title={`Lọc xem riêng đội ${itemTeam.name}`}
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <IconButton
                              size="small"
                              title="Tải về"
                              onClick={() => handleDownload(item.id, item.file_name)}
                            >
                              <Download fontSize="small" />
                            </IconButton>
                            {isOwnerOrBtc && canMutateEvidence && (
                              <IconButton
                                size="small"
                                color="error"
                                title={deletingIds.has(item.id) ? 'Đang xóa...' : 'Xóa khỏi danh sách'}
                                onClick={() => handleDelete(item.id)}
                                disabled={deletingIds.has(item.id)}
                              >
                                {deletingIds.has(item.id) ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <Delete fontSize="small" />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
          {publicEvidence.length > 0 && (
            <Paper
              elevation={0}
              sx={(theme) => ({
                ...getTablePaperSx(theme),
                p: 3,
                mt: 3,
              })}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Chứng cứ BTC công khai
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tài liệu từ đội khác/BTC đã được cấp quyền xem cho đội bạn.
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  label={`${publicEvidence.length} chứng cứ`}
                />
              </Box>
              <TableContainer sx={{ maxHeight: 320, overflowY: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: 'background.paper' }}>Tiêu đề</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', minWidth: 140 }}>Nguồn</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'background.paper', minWidth: 90 }}>
                        Thao tác
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {publicEvidence.map((item) => {
                      const sourceTeam = teams.find((t) => t.id === item.team_id)
                      return (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500} noWrap title={item.title}>
                              {item.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {item.file_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={sourceTeam?.name || 'BTC'}
                              color="default"
                              variant="outlined"
                              sx={{ maxWidth: 160 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <IconButton
                              size="small"
                              title="Tải về"
                              onClick={() => handleDownload(item.id, item.file_name)}
                            >
                              <Download fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}

      {selectedCaseId && !skipAiSuggestions && (
        <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
            Gợi ý từ tài liệu vụ (RAG - BTC / Giám khảo)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Chỉ BTC / Giám khảo thấy khối này. Gợi ý chỉ hỗ trợ tra cứu, không thay thế quyết định.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Từ khóa / câu hỏi"
              value={aiKeyword}
              onChange={(e) => setAiKeyword(e.target.value)}
              sx={{ minWidth: 200, flex: 1 }}
            />
            <Button
              variant="outlined"
              disabled={aiLoading}
              onClick={() =>
                fetchAiSuggestions({
                  case_id: selectedCaseId,
                  ...(aiKeyword.trim() ? { q: aiKeyword.trim() } : {}),
                })
              }
            >
              Tìm gợi ý
            </Button>
          </Box>
          {aiSuggestionsError ? (
            <Alert severity="warning">
              {(aiSuggestionsErr as { data?: { detail?: string } })?.data?.detail ||
                'Không tải được gợi ý (kiểm tra phạm vi phân công).'}
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {aiSuggestions?.notice}
              </Alert>
              {aiLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {(aiSuggestions?.suggestions || []).map((s) => (
                <Paper
                  key={s.id}
                  variant="outlined"
                  sx={{ p: 1.5, mb: 1.5, overflow: 'hidden', wordBreak: 'break-word' }}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {s.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {s.detail}
                  </Typography>
                  {s.source_document_id ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`document_id: ${s.source_document_id}`}
                      sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                    />
                  ) : null}
                </Paper>
              ))}
            </>
          )}
        </Paper>
      )}


      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="md" fullWidth>
        <DialogTitle>Upload Chứng cứ</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Cuộc thi *</InputLabel>
              <Select
                value={uploadForm.case_id}
                label="Cuộc thi *"
                onChange={(e) => setUploadForm({ ...uploadForm, case_id: e.target.value })}
                required
              >
                {userCases.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {formatCaseDisplay(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Tiêu đề *"
              value={uploadForm.title}
              onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Mô tả"
              value={uploadForm.description}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>Danh mục</InputLabel>
              <Select
                value={uploadForm.category}
                label="Danh mục"
                onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
              >
                <MenuItem value="">Không chọn</MenuItem>
                {EVIDENCE_CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(() => {
              const selectedCase = cases.find((c) => c.id === uploadForm.case_id)
              const team = teams.find((t) => t.id === uploadForm.team_id)
              const previewTags: string[] = []
              if (team) previewTags.push(`team:${team.name}`)
              if (selectedCase) previewTags.push(`case:${formatCaseDisplay(selectedCase)}`)
              if (selectedCase?.case_number) previewTags.push(`case_number:${selectedCase.case_number}`)
              if (previewTags.length === 0) return null
              return (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Tags tự động đính kèm
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {previewTags.map((t) => (
                      <Chip key={t} size="small" label={t} variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )
            })()}
            <Button variant="outlined" component="label" fullWidth startIcon={<Upload />}>
              Chọn File
              <input
                type="file"
                hidden
                onChange={handleFileChange}
                accept=".pdf,.docx,.jpg,.jpeg,.png,.mp4,.mp3"
              />
            </Button>
            {uploadForm.file && (
              <Typography variant="body2">
                Đã chọn: {uploadForm.file.name} ({(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
            {/* Chỉ hiện cảnh báo khi đã quá hạn — đỡ dư thừa với banner ngoài */}
            {uploadDialogDeadline && !uploadDialogDeadline.submission_open && (
              <Alert severity="error" variant="outlined">
                {uploadDialogDeadline.message}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>Hủy</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={isUploading || !uploadDialogSubmissionOpen}
          >
            {isUploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Evidence Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {viewingEvidence && getFileIcon(viewingEvidence.mime_type)}
            <Typography variant="h6">Chi tiết Chứng cứ</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingEvidence && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Tiêu đề
                </Typography>
                <Typography variant="body1">{viewingEvidence.title}</Typography>
              </Box>
              {viewingEvidence.description && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Mô tả
                  </Typography>
                  <Typography variant="body1">{viewingEvidence.description}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Cuộc thi
                </Typography>
                <Typography variant="body1">
                  {cases.find((c) => c.id === viewingEvidence.case_id) 
                    ? formatCaseDisplay(cases.find((c) => c.id === viewingEvidence.case_id)!)
                    : viewingEvidence.case_id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Đội thi
                </Typography>
                <Typography variant="body1">
                  {(() => {
                    const team = teams.find((t) => t.id === viewingEvidence.team_id)
                    return team ? team.name : (isLoadingTeams ? 'Đang tải...' : viewingEvidence.team_id)
                  })()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  File
                </Typography>
                <Typography variant="body1">{viewingEvidence.file_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Kích thước: {(viewingEvidence.file_size / 1024 / 1024).toFixed(2)} MB • {viewingEvidence.mime_type}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {viewingEvidence.mime_type?.startsWith('image/') && (
                    <Box
                      component="img"
                      src={viewingEvidence.file_url}
                      alt={viewingEvidence.file_name}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 400,
                        borderRadius: 1,
                        border: '1px solid #ddd',
                      }}
                    />
                  )}

                  {viewingEvidence.mime_type?.startsWith('audio/') && (
                    <audio
                      controls
                      style={{ width: '100%' }}
                      src={viewingEvidence.file_url}
                    >
                      Trình duyệt không hỗ trợ audio preview.
                    </audio>
                  )}

                  {viewingEvidence.mime_type?.startsWith('video/') && (
                    <video
                      controls
                      style={{
                        width: '100%',
                        maxHeight: 400,
                        borderRadius: 8,
                      }}
                      src={viewingEvidence.file_url}
                    >
                      Trình duyệt không hỗ trợ video preview.
                    </video>
                  )}

                  {(viewingEvidence.mime_type?.includes('pdf') ||
                    viewingEvidence.category === 'text') && (
                    <iframe
                      src={viewingEvidence.file_url}
                      title={viewingEvidence.file_name}
                      style={{
                        width: '100%',
                        height: 500,
                        border: '1px solid #ddd',
                        borderRadius: 8,
                      }}
                    />
                  )}
                </Box>
              </Box>
              {viewingEvidence.category && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Danh mục
                  </Typography>
                  <Chip label={viewingEvidence.category} size="small" color="primary" />
                </Box>
              )}
              {viewingEvidence.tags && viewingEvidence.tags.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {viewingEvidence.tags
                      .filter((tag: string) => {
                        // Bỏ qua team_id và case_id nếu đã có team: và case:
                        if (tag.startsWith('team_id:') && viewingEvidence.tags.some((t: string) => t.startsWith('team:'))) {
                          return false
                        }
                        if (tag.startsWith('case_id:') && viewingEvidence.tags.some((t: string) => t.startsWith('case:'))) {
                          return false
                        }
                        return true
                      })
                      .map((tag: string, idx: number) => {
                        // Format tag để hiển thị tên thay vì ID
                        let displayTag = tag
                        if (tag.startsWith('team:')) {
                          displayTag = tag.replace('team:', 'Đội: ')
                        } else if (tag.startsWith('case:')) {
                          displayTag = tag.replace('case:', 'Cuộc thi: ')
                        } else if (tag.startsWith('case_id:')) {
                          const caseId = tag.replace('case_id:', '')
                          const caseItem = cases.find((c) => c.id === caseId)
                          displayTag = caseItem ? `Cuộc thi: ${formatCaseDisplay(caseItem)}` : tag
                        } else if (tag.startsWith('team_id:')) {
                          const teamId = tag.replace('team_id:', '')
                          const team = teams.find((t) => t.id === teamId)
                          displayTag = team ? `Đội: ${team.name}` : tag
                        } else if (tag.startsWith('case_number:')) {
                          displayTag = tag.replace('case_number:', 'Số vụ án: ')
                        }
                        return (
                          <Chip key={idx} label={displayTag} size="small" variant="outlined" />
                        )
                      })}
                  </Box>
                </Box>
              )}
              {viewingEvidence.uploaded_at && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Ngày upload
                  </Typography>
                  <Typography variant="body1">
                    {new Date(viewingEvidence.uploaded_at).toLocaleString('vi-VN')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {viewingEvidence && (
            <Button
              startIcon={<Download />}
              onClick={() => handleDownload(viewingEvidence.id, viewingEvidence.file_name)}
            >
              Download
            </Button>
          )}
          <Button onClick={handleCloseViewDialog}>Đóng</Button>
        </DialogActions>
      </Dialog>

      

      {/* Document Preview Dialog — DOCX render qua mammoth, PDF/image dùng iframe */}
      <Dialog
        open={previewDoc.open}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap>{previewDoc.title}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {previewDoc.fileName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Download />}
              onClick={() => {
                if (!previewDoc.blobUrl && previewDoc.html) {
                  // Render từ DOCX → cũng cho phép tải file gốc
                }
                // Tải về lại file gốc thông qua document_id — nhưng dialog không giữ id
                // Đơn giản: trigger download bằng blob đã có nếu là PDF/image
                if (previewDoc.blobUrl) {
                  const a = document.createElement('a')
                  a.href = previewDoc.blobUrl
                  a.download = previewDoc.fileName
                  a.click()
                }
              }}
              sx={{ textTransform: 'none' }}
            >
              Tải về
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
          {previewDoc.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : previewDoc.error ? (
            <Alert severity="error" sx={{ m: 2 }}>{previewDoc.error}</Alert>
          ) : previewDoc.html ? (
            <Box
              sx={{
                p: 4,
                height: '100%',
                overflow: 'auto',
                bgcolor: '#fafafa',
                '& .docx-content': {
                  maxWidth: 800,
                  mx: 'auto',
                  p: 4,
                  bgcolor: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  borderRadius: 1,
                  fontFamily: 'Times New Roman, serif',
                  lineHeight: 1.6,
                  '& h1, & h2, & h3': { fontWeight: 700, my: 1.5 },
                  '& p': { my: 1 },
                  '& table': { borderCollapse: 'collapse', my: 2, width: '100%' },
                  '& td, & th': { border: '1px solid #ddd', p: 1 },
                  '& img': { maxWidth: '100%', height: 'auto' },
                },
              }}
            >
              <div className="docx-content" dangerouslySetInnerHTML={{ __html: previewDoc.html }} />
            </Box>
          ) : previewDoc.blobUrl ? (
            <Box sx={{ height: '100%' }}>
              {previewDoc.mimeType.startsWith('image/') ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', bgcolor: '#1a1a1a', p: 2 }}>
                  <img src={previewDoc.blobUrl} alt={previewDoc.title} style={{ maxWidth: '100%', maxHeight: '100%' }} />
                </Box>
              ) : (
                <iframe
                  src={previewDoc.blobUrl}
                  title={previewDoc.title}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              )}
            </Box>
          ) : (
            <Alert severity="info" sx={{ m: 2 }}>Không có nội dung để hiển thị.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Đóng</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
